import React, { useMemo, useState } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { api } from '../../../lib/api';
import { useToast } from '../../../components/ui/Toast';
import { Button, Card } from '../../../components/ui/Kit';
import Modal from '../../../components/ui/Modal';
import SafeIcon from '../../../common/SafeIcon';
import { calculateInsuranceNeed } from '../../../lib/financialAnalysisUtils';

const POLICY_TYPES = [
  'Term Life',
  'Whole Life',
  'Universal Life',
  'Variable Life',
];

const DEFAULT_FINAL_EXPENSES = 25000;

const cellInput =
  'w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(val) || 0);
}

function policyPayloadFromRow(row) {
  return {
    carrier: row.carrier || '',
    policy_type: row.policy_type || 'Term Life',
    coverage_amount: Number(row.coverage_amount) || 0,
    annual_premium:
      row.annual_premium === '' || row.annual_premium == null
        ? null
        : Number(row.annual_premium),
    issue_date: row.issue_date || null,
    beneficiary: row.beneficiary || '',
  };
}

function resolveFinalExpenses(override) {
  if (override === '' || override == null) return DEFAULT_FINAL_EXPENSES;
  return Number(override);
}

export default function InsuranceTab({
  analysisId,
  agencyId,
  analysis,
  incomeSources,
  assets,
  insurancePolicies,
  setInsurancePolicies,
  scheduleRowSave,
  scheduleParentSave,
  setSaveStatus,
  markSavedBriefly,
  policiesRef,
  parentFieldsRef,
}) {
  const { addToast } = useToast();
  const [adding, setAdding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const yearsToReplace = parentFieldsRef.current?.years_to_replace ?? analysis.years_to_replace ?? 20;
  const finalExpensesOverride =
    parentFieldsRef.current?.final_expenses_override !== undefined
      ? parentFieldsRef.current.final_expenses_override
      : analysis.final_expenses_override;
  const educationFund =
    parentFieldsRef.current?.education_fund !== undefined
      ? parentFieldsRef.current.education_fund
      : analysis.education_fund ?? 0;

  const need = useMemo(
    () =>
      calculateInsuranceNeed({
        incomeSources,
        assets,
        insurancePolicies,
        yearsToReplace: yearsToReplace || 20,
        finalExpenses: resolveFinalExpenses(finalExpensesOverride),
        educationFund: Number(educationFund) || 0,
      }),
    [incomeSources, assets, insurancePolicies, yearsToReplace, finalExpensesOverride, educationFund],
  );

  const onPolicyChange = (rowId, field, value) => {
    setInsurancePolicies((prev) => {
      const next = prev.map((r) => (r.id === rowId ? { ...r, [field]: value } : r));
      policiesRef.current = next;
      return next;
    });
    scheduleRowSave('policies', rowId, () => {
      const row = (policiesRef.current || []).find((r) => r.id === rowId);
      return policyPayloadFromRow(row || {});
    });
  };

  const onParentChange = (field, value) => {
    const nextFields = {
      ...(parentFieldsRef.current || {}),
      years_to_replace: parentFieldsRef.current?.years_to_replace ?? analysis.years_to_replace ?? 20,
      final_expenses_override:
        parentFieldsRef.current?.final_expenses_override !== undefined
          ? parentFieldsRef.current.final_expenses_override
          : analysis.final_expenses_override,
      education_fund:
        parentFieldsRef.current?.education_fund !== undefined
          ? parentFieldsRef.current.education_fund
          : analysis.education_fund ?? 0,
      [field]: value,
    };
    parentFieldsRef.current = nextFields;
    // Force re-render via setAnalysis-compatible updater on parent through schedule
    scheduleParentSave(() => ({
      years_to_replace: Number(nextFields.years_to_replace) || 20,
      final_expenses_override:
        nextFields.final_expenses_override === '' || nextFields.final_expenses_override == null
          ? null
          : Number(nextFields.final_expenses_override),
      education_fund: Number(nextFields.education_fund) || 0,
    }));
  };

  // Local display state synced when parent fields change (parentFieldsRef alone won't re-render)
  const [, bump] = useState(0);
  const setParentField = (field, value) => {
    onParentChange(field, value);
    bump((n) => n + 1);
  };

  const addPolicy = async () => {
    setAdding(true);
    try {
      const row = await api.financialAnalysis.insurancePolicies.create(analysisId, {
        agency_id: agencyId,
        carrier: '',
        policy_type: 'Term Life',
        coverage_amount: 0,
        annual_premium: null,
        issue_date: null,
        beneficiary: '',
      });
      setInsurancePolicies((prev) => {
        const next = [...prev, row];
        policiesRef.current = next;
        return next;
      });
      markSavedBriefly();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not add policy', message: err?.message });
    } finally {
      setAdding(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.financialAnalysis.insurancePolicies.delete(deleteTarget.id);
      setInsurancePolicies((prev) => {
        const next = prev.filter((r) => r.id !== deleteTarget.id);
        policiesRef.current = next;
        return next;
      });
      setSaveStatus('saved');
      markSavedBriefly();
      setDeleteTarget(null);
    } catch (err) {
      addToast({ type: 'error', title: 'Could not delete policy', message: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  const displayYears = yearsToReplace ?? 20;
  const displayFinal =
    finalExpensesOverride === '' || finalExpensesOverride == null ? '' : finalExpensesOverride;
  const displayEducation = educationFund ?? 0;

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Existing Policies</h2>
          <Button size="sm" variant="outline" onClick={addPolicy} disabled={adding} isLoading={adding}>
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
            Add Policy
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3 font-medium">Carrier</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium w-36">Coverage</th>
                <th className="px-4 py-3 font-medium w-32">Premium / yr</th>
                <th className="px-4 py-3 font-medium w-36">Issue Date</th>
                <th className="px-4 py-3 font-medium">Beneficiary</th>
                <th className="px-4 py-3 font-medium w-12" />
              </tr>
            </thead>
            <tbody>
              {(insurancePolicies || []).length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">
                    No policies yet. Add existing coverage to improve the needs analysis.
                  </td>
                </tr>
              ) : (
                insurancePolicies.map((row) => (
                  <tr key={row.id} className="border-b border-gray-50">
                    <td className="px-4 py-2">
                      <input
                        className={cellInput}
                        value={row.carrier || ''}
                        placeholder="Carrier"
                        onChange={(e) => onPolicyChange(row.id, 'carrier', e.target.value)}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <select
                        className={cellInput}
                        value={row.policy_type || 'Term Life'}
                        onChange={(e) => onPolicyChange(row.id, 'policy_type', e.target.value)}
                      >
                        {POLICY_TYPES.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={`${cellInput} tabular-nums`}
                        value={row.coverage_amount ?? 0}
                        onChange={(e) =>
                          onPolicyChange(
                            row.id,
                            'coverage_amount',
                            e.target.value === '' ? 0 : Number(e.target.value),
                          )
                        }
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={`${cellInput} tabular-nums`}
                        value={row.annual_premium ?? ''}
                        placeholder="—"
                        onChange={(e) =>
                          onPolicyChange(
                            row.id,
                            'annual_premium',
                            e.target.value === '' ? null : Number(e.target.value),
                          )
                        }
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="date"
                        className={cellInput}
                        value={row.issue_date || ''}
                        onChange={(e) => onPolicyChange(row.id, 'issue_date', e.target.value || null)}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        className={cellInput}
                        value={row.beneficiary || ''}
                        placeholder="Beneficiary"
                        onChange={(e) => onPolicyChange(row.id, 'beneficiary', e.target.value)}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                        onClick={() => setDeleteTarget(row)}
                        aria-label="Delete policy"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-5 space-y-5">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Needs Analysis</h2>
          <p className="text-xs text-gray-500 mt-1">
            Liquid assets, retirement accounts, and existing coverage are pulled automatically from the
            Balance Sheet and policies above — not entered separately here.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Years to Replace Income</label>
            <input
              type="number"
              min="1"
              className={cellInput}
              value={displayYears}
              onChange={(e) =>
                setParentField('years_to_replace', e.target.value === '' ? 20 : Number(e.target.value))
              }
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Final Expenses Override</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className={cellInput}
              value={displayFinal}
              placeholder="25000 (default)"
              onChange={(e) =>
                setParentField(
                  'final_expenses_override',
                  e.target.value === '' ? null : Number(e.target.value),
                )
              }
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Education Fund</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className={cellInput}
              value={displayEducation}
              onChange={(e) =>
                setParentField('education_fund', e.target.value === '' ? 0 : Number(e.target.value))
              }
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <ReadOnlyStat
            label="Annual Income"
            hint="From Cashflow income sources"
            value={formatCurrency(need.annualIncome)}
          />
          <ReadOnlyStat
            label="Existing Coverage"
            hint="Sum of policy coverage amounts"
            value={formatCurrency(need.existingCoverage)}
          />
          <ReadOnlyStat
            label="Liquid Assets"
            hint="Checking + savings + investments"
            value={formatCurrency(need.liquidAssets)}
          />
          <ReadOnlyStat
            label="Retirement Accounts"
            hint="Retirement / 401k / IRA / Roth"
            value={formatCurrency(need.retirementAccounts)}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ReadOnlyStat label="Total Needs" value={formatCurrency(need.totalNeeds)} />
          <ReadOnlyStat label="Total Resources" value={formatCurrency(need.totalResources)} />
          <ReadOnlyStat
            label="Additional Coverage Needed"
            value={formatCurrency(need.additionalCoverageNeeded)}
          />
        </div>

        <div className="rounded-xl border-2 border-primary/30 bg-primary/5 px-5 py-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">Coverage Gap</p>
          <p className="text-3xl font-bold text-gray-900 tabular-nums">{formatCurrency(need.coverageGap)}</p>
          <p className="text-xs text-gray-500 mt-2">
            Additional coverage needed after accounting for existing policies and balance-sheet resources
          </p>
        </div>
      </Card>

      <Modal
        isOpen={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="Delete policy?"
      >
        <p className="text-sm text-gray-500 mb-5">
          Remove this policy
          {deleteTarget?.carrier ? (
            <>
              {' '}
              (<span className="font-medium text-gray-800">{deleteTarget.carrier}</span>)
            </>
          ) : null}
          ? This cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={confirmDelete} isLoading={deleting}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function ReadOnlyStat({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <p className="text-base font-semibold text-gray-900 tabular-nums mt-1">{value}</p>
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}
